import { Component } from '@theme/component';

/**
 * Checks a 6-digit Indian PIN code against the serviceable list from block
 * settings. Entries are exact PIN codes or prefixes ending in `*` (e.g. `302*`).
 * The result is announced through the block's single status region.
 *
 * @typedef {object} PincodeCheckRefs
 * @property {HTMLInputElement} input - The PIN code field.
 * @property {HTMLElement} status - Live region for the result.
 *
 * @extends {Component<PincodeCheckRefs>}
 */
class PincodeCheck extends Component {
  requiredRefs = ['input', 'status'];

  /** @type {string[]} */
  get #rules() {
    return (this.dataset.pincodes || '')
      .split(/[\s,]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  /**
   * @param {string} pincode
   */
  #isServiceable(pincode) {
    const rules = this.#rules;
    // No list configured: every valid PIN code is accepted.
    if (rules.length === 0) return true;

    for (const rule of rules) {
      if (rule.endsWith('*')) {
        if (pincode.startsWith(rule.slice(0, -1))) return true;
      } else if (pincode === rule) {
        return true;
      }
    }
    return false;
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = (event) => {
    event.preventDefault();

    const pincode = this.refs.input.value.replace(/\D/g, '');
    const { status } = this.refs;
    status.classList.remove('pincode-check__status--success', 'pincode-check__status--error');

    if (pincode.length !== 6) {
      this.refs.input.setAttribute('aria-invalid', 'true');
      status.textContent = this.dataset.invalidMessage || '';
      status.classList.add('pincode-check__status--error');
      return;
    }

    this.refs.input.removeAttribute('aria-invalid');
    const serviceable = this.#isServiceable(pincode);
    status.textContent = ((serviceable ? this.dataset.successMessage : this.dataset.errorMessage) || '').replaceAll(
      '{pincode}',
      pincode
    );
    status.classList.add(serviceable ? 'pincode-check__status--success' : 'pincode-check__status--error');
  };
}

if (!customElements.get('pincode-check')) {
  customElements.define('pincode-check', PincodeCheck);
}
